import { useEffect, useState } from "react";
import { api, formatINR, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, Edit2, Trash2, Car, Wrench, ClipboardCheck, ShieldCheck, X, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { Modal, In } from "@/pages/Workers";

const TYPES = ["Car", "Bike", "Truck", "Tractor", "Pickup", "Other"];

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [serviceFor, setServiceFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const [v, e, a] = await Promise.all([
      api.get("/vehicles"),
      api.get("/employees"),
      api.get("/vehicles/alerts/expiring"),
    ]);
    setItems(v.data); setEmployees(e.data); setAlerts(a.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this vehicle and all its records?")) return;
    await api.delete(`/vehicles/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Fleet & Facilities</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Company Vehicles</h1>
          <p className="text-stone-600 mt-1 text-sm">Vehicle master, insurance, service history & inspections.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadXlsx("/vehicles/export.xlsx", "vehicles_register.xlsx")}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
                  style={{ background: "#1B4332" }} data-testid="export-vehicles-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-vehicle-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Vehicle
          </button>
        </div>
      </header>

      {alerts.length > 0 && (
        <div className="card-flat" style={{ background: "#FCE7C9", borderColor: "#E5A453" }} data-testid="vehicle-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: "#9A6020" }} />
            <div className="kpi-label" style={{ color: "#9A6020" }}>Expired or expiring within 30 days</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {alerts.map((a) => {
              const expired = a.status === "expired" || a.days_left < 0;
              return (
                <div key={`${a.vehicle_id}-${a.field}`} className="bg-white rounded-md p-2 border"
                     style={{ borderColor: expired ? "#DC2626" : "#E5A453" }}
                     data-testid={`vehicle-alert-${a.vehicle_id}-${a.field}`}>
                  <div className="flex items-center gap-1.5">
                    <div className="font-mono font-semibold">{a.registration_no}</div>
                    {expired && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: "#FEE2E2", color: "#991B1B" }}>EXPIRED</span>
                    )}
                  </div>
                  <div className="text-stone-600 mt-0.5">
                    {a.field} ·{" "}
                    {expired
                      ? <span className="text-red-700 font-medium">{Math.abs(a.days_left)}d overdue</span>
                      : <span>{a.days_left}d left</span>}
                    {" "}({a.expires_on})
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-grid">
          {items.length === 0 && <div className="text-sm text-stone-500">No vehicles yet.</div>}
          {items.map((v) => (
            <div key={v.id} className="card-flat" data-testid={`vehicle-${v.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary-light)" }}>
                  <Car className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold font-mono">{v.registration_no}</div>
                  <div className="text-xs text-stone-500">{v.make} {v.model}</div>
                </div>
                <span className="status-pill capitalize" style={{ background: v.status === "Active" ? "var(--color-primary-light)" : "#F4F1DE", color: v.status === "Active" ? "var(--color-primary)" : "#57534E" }}>
                  {v.status}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <Item label="Type" v={v.vehicle_type} />
                <Item label="Assigned" v={v.assigned_to_name || "Unassigned"} />
                <Item label="Engine No." v={v.engine_no || "—"} />
                <Item label="Insurance Exp." v={v.insurance_expiry || "—"} />
              </div>
              <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-2">
                <button onClick={() => setServiceFor(v)} data-testid={`service-vehicle-${v.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-stone-200 text-xs font-medium hover:border-primary">
                  <Wrench className="w-3.5 h-3.5" /> Service & Inspection
                </button>
                <button onClick={() => setEditing(v)} className="p-1.5 rounded hover:bg-stone-100"><Edit2 className="w-4 h-4 text-stone-500" /></button>
                <button onClick={() => remove(v.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showAdd || editing) && (
        <VehicleForm employees={employees} initial={editing}
                     onClose={() => { setShowAdd(false); setEditing(null); }}
                     onSaved={() => { setShowAdd(false); setEditing(null); load(); }} />
      )}
      {serviceFor && <ServiceInspectionPanel vehicle={serviceFor} onClose={() => { setServiceFor(null); load(); }} />}
    </div>
  );
}

function Item({ label, v }) {
  return <div><div className="kpi-label">{label}</div><div className="text-sm font-medium mt-0.5">{v}</div></div>;
}

function VehicleForm({ employees, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    registration_no: "", vehicle_type: "Car", make: "", model: "",
    engine_no: "", chassis_no: "", purchase_date: "",
    status: "Active", insurance_provider: "", policy_no: "",
    insurance_expiry: "", fitness_expiry: "", puc_expiry: "",
    assigned_to: "", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const body = { ...form, assigned_to: form.assigned_to || null };
      ["purchase_date", "insurance_expiry", "fitness_expiry", "puc_expiry"].forEach((k) => {
        if (!body[k]) body[k] = null;
      });
      if (initial) await api.put(`/vehicles/${initial.id}`, body);
      else await api.post("/vehicles", body);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <Modal title={initial ? "Edit Vehicle" : "Add Vehicle"} onClose={onClose} testid="vehicle-modal">
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <In label="Registration No." v={form.registration_no} on={(v) => setForm({ ...form, registration_no: v })} required placeholder="KA01AB1234" />
        <label className="block"><div className="kpi-label mb-1.5">Vehicle Type</div>
          <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <In label="Make" v={form.make} on={(v) => setForm({ ...form, make: v })} placeholder="Mahindra" />
        <In label="Model" v={form.model} on={(v) => setForm({ ...form, model: v })} placeholder="Bolero" />
        <In label="Engine No." v={form.engine_no} on={(v) => setForm({ ...form, engine_no: v })} />
        <In label="Chassis No." v={form.chassis_no} on={(v) => setForm({ ...form, chassis_no: v })} />
        <In label="Purchase Date" type="date" v={form.purchase_date} on={(v) => setForm({ ...form, purchase_date: v })} />
        <label className="block"><div className="kpi-label mb-1.5">Status</div>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option>Active</option><option>Sold</option><option>Scrapped</option>
          </select>
        </label>
        <In label="Insurance Provider" v={form.insurance_provider} on={(v) => setForm({ ...form, insurance_provider: v })} />
        <In label="Policy No." v={form.policy_no} on={(v) => setForm({ ...form, policy_no: v })} />
        <In label="Insurance Expiry" type="date" v={form.insurance_expiry} on={(v) => setForm({ ...form, insurance_expiry: v })} />
        <In label="Fitness Expiry" type="date" v={form.fitness_expiry} on={(v) => setForm({ ...form, fitness_expiry: v })} />
        <In label="PUC Expiry" type="date" v={form.puc_expiry} on={(v) => setForm({ ...form, puc_expiry: v })} />
        <label className="block"><div className="kpi-label mb-1.5">Assigned To</div>
          <select value={form.assigned_to || ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option value="">Unassigned</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label className="col-span-2 block"><div className="kpi-label mb-1.5">Remarks</div>
          <textarea rows="2" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>
        {err && <div className="col-span-2 text-sm px-3 py-2 rounded-md" style={{ background: "#FADED7", color: "#9A3412" }}>{err}</div>}
        <button type="submit" disabled={busy} data-testid="save-vehicle-button"
                className="col-span-2 py-2 mt-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}

function ServiceInspectionPanel({ vehicle, onClose }) {
  const [tab, setTab] = useState("service");
  const [services, setServices] = useState([]);
  const [inspections, setInspections] = useState([]);
  const load = async () => {
    const [s, i] = await Promise.all([
      api.get(`/vehicles/${vehicle.id}/services`),
      api.get(`/vehicles/${vehicle.id}/inspections`),
    ]);
    setServices(s.data); setInspections(i.data);
  };
  useEffect(() => { load(); }, [vehicle]);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex" data-testid="service-panel">
      <div className="flex-1" onClick={onClose}></div>
      <div className="w-full max-w-2xl bg-white p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
            <div>
              <div className="font-mono font-bold">{vehicle.registration_no}</div>
              <div className="text-xs text-stone-500">{vehicle.make} {vehicle.model}</div>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="card-flat" style={{ padding: 16, marginBottom: 16 }}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="kpi-label">Make / Model</div><div className="font-medium mt-0.5">{vehicle.make} {vehicle.model}</div></div>
            <div><div className="kpi-label">Engine No.</div><div className="font-medium mt-0.5">{vehicle.engine_no || "—"}</div></div>
            <div><div className="kpi-label flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Insurance</div><div className="font-medium mt-0.5">{vehicle.insurance_provider || "—"}</div></div>
            <div><div className="kpi-label">Insurance Expiry</div><div className="font-medium mt-0.5">{vehicle.insurance_expiry || "—"}</div></div>
            <div><div className="kpi-label">Fitness Expiry</div><div className="font-medium mt-0.5">{vehicle.fitness_expiry || "—"}</div></div>
            <div><div className="kpi-label">PUC Expiry</div><div className="font-medium mt-0.5">{vehicle.puc_expiry || "—"}</div></div>
          </div>
        </div>

        <div className="flex gap-1 bg-stone-100 rounded-md p-1 mb-4">
          {[["service", "Service", Wrench], ["inspection", "Inspection", ClipboardCheck]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${tab === k ? "bg-white shadow" : "text-stone-500"}`}>
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {tab === "service" ? (
          <ServiceTab vehicleId={vehicle.id} services={services} reload={load} />
        ) : (
          <InspectionTab vehicleId={vehicle.id} inspections={inspections} reload={load} />
        )}
      </div>
    </div>
  );
}

function ServiceTab({ vehicleId, services, reload }) {
  const [form, setForm] = useState({
    service_date: new Date().toISOString().slice(0, 10),
    service_type: "Oil change", odometer_km: 0, cost: 0,
    vendor: "", notes: "", next_service_date: "",
  });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post(`/vehicles/${vehicleId}/services`, {
      ...form, odometer_km: Number(form.odometer_km), cost: Number(form.cost),
      next_service_date: form.next_service_date || null,
    });
    setForm({ ...form, service_type: "Oil change", odometer_km: 0, cost: 0, vendor: "", notes: "" });
    setBusy(false); reload();
  };
  const remove = async (id) => { await api.delete(`/vehicle-services/${id}`); reload(); };
  return (
    <>
      <form onSubmit={add} className="card-flat" style={{ padding: 16, marginBottom: 12 }}>
        <div className="grid grid-cols-2 gap-3">
          <In label="Service Date" type="date" v={form.service_date} on={(v) => setForm({ ...form, service_date: v })} required />
          <In label="Service Type" v={form.service_type} on={(v) => setForm({ ...form, service_type: v })} placeholder="Oil change" />
          <In label="Odometer (km)" type="number" v={form.odometer_km} on={(v) => setForm({ ...form, odometer_km: v })} />
          <In label="Cost (₹)" type="number" v={form.cost} on={(v) => setForm({ ...form, cost: v })} />
          <In label="Vendor" v={form.vendor} on={(v) => setForm({ ...form, vendor: v })} />
          <In label="Next Service" type="date" v={form.next_service_date} on={(v) => setForm({ ...form, next_service_date: v })} />
        </div>
        <label className="block mt-3"><div className="kpi-label mb-1.5">Notes</div>
          <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
        </label>
        <button type="submit" disabled={busy} data-testid="add-service-button"
                className="mt-3 w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Adding…" : "Add Service Record"}
        </button>
      </form>
      <div className="space-y-2">
        {services.length === 0 && <div className="text-sm text-stone-500 text-center py-4">No service records yet.</div>}
        {services.map((s) => (
          <div key={s.id} className="card-flat" style={{ padding: 12 }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-sm">{s.service_type}</div>
                <div className="text-xs text-stone-500">{s.service_date} · {s.odometer_km?.toLocaleString("en-IN")} km{s.vendor ? ` · ${s.vendor}` : ""}</div>
                {s.notes && <div className="text-xs text-stone-600 mt-1 italic">"{s.notes}"</div>}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="kpi-label">Cost</div>
                  <div className="font-semibold">{formatINR(s.cost)}</div>
                </div>
                <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function InspectionTab({ vehicleId, inspections, reload }) {
  const [form, setForm] = useState({
    inspection_date: new Date().toISOString().slice(0, 10),
    inspector: "", odometer_km: 0, findings: "",
    rating: "Pass", next_inspection_date: "",
  });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post(`/vehicles/${vehicleId}/inspections`, {
      ...form, odometer_km: Number(form.odometer_km),
      next_inspection_date: form.next_inspection_date || null,
    });
    setForm({ ...form, inspector: "", findings: "", rating: "Pass" });
    setBusy(false); reload();
  };
  const remove = async (id) => { await api.delete(`/vehicle-inspections/${id}`); reload(); };
  const COL = { Pass: "var(--color-primary)", Fail: "#9A3412", "Needs Attention": "#9A6020" };
  return (
    <>
      <form onSubmit={add} className="card-flat" style={{ padding: 16, marginBottom: 12 }}>
        <div className="grid grid-cols-2 gap-3">
          <In label="Inspection Date" type="date" v={form.inspection_date} on={(v) => setForm({ ...form, inspection_date: v })} required />
          <In label="Inspector" v={form.inspector} on={(v) => setForm({ ...form, inspector: v })} />
          <In label="Odometer (km)" type="number" v={form.odometer_km} on={(v) => setForm({ ...form, odometer_km: v })} />
          <label className="block"><div className="kpi-label mb-1.5">Rating</div>
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              <option>Pass</option><option>Needs Attention</option><option>Fail</option>
            </select>
          </label>
          <In label="Next Inspection" type="date" v={form.next_inspection_date} on={(v) => setForm({ ...form, next_inspection_date: v })} />
        </div>
        <label className="block mt-3"><div className="kpi-label mb-1.5">Findings</div>
          <textarea rows="2" value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
        </label>
        <button type="submit" disabled={busy} data-testid="add-inspection-button"
                className="mt-3 w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Adding…" : "Add Inspection"}
        </button>
      </form>
      <div className="space-y-2">
        {inspections.length === 0 && <div className="text-sm text-stone-500 text-center py-4">No inspection records yet.</div>}
        {inspections.map((s) => (
          <div key={s.id} className="card-flat" style={{ padding: 12 }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{s.inspector || "—"}</span>
                  <span className="status-pill" style={{ background: "var(--color-primary-light)", color: COL[s.rating] }}>{s.rating}</span>
                </div>
                <div className="text-xs text-stone-500">{s.inspection_date} · {s.odometer_km?.toLocaleString("en-IN")} km</div>
                {s.findings && <div className="text-xs text-stone-600 mt-1 italic">"{s.findings}"</div>}
              </div>
              <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
